import React, { useEffect, useState } from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { SubscriptionPlan } from '../../types/subscription';
import { Check, Sparkles, AlertTriangle, CreditCard, Lock, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

// Plan tier order for comparison
const PLAN_TIERS: Record<string, number> = {
  starter: 1,
  professional: 2,
  enterprise: 3,
};

const SubscriptionPage: React.FC = () => {
  const { subscription, usageStats, refreshSubscription } = useSubscription();
  const { user } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmingPlan, setConfirmingPlan] = useState<{ plan: SubscriptionPlan; isDowngrade: boolean } | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  // Check if user has Admin role
  const isAdmin = user?.roleName === 'Admin';

  useEffect(() => {
    loadPlans();
    handleUrlParameters();
  }, []);

  const handleUrlParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success === 'true') {
      toast.success('Subscription updated successfully!', {
        duration: 5000,
        icon: '🎉',
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled === 'true') {
      toast('Checkout canceled. Your subscription remains unchanged.', {
        duration: 4000,
        icon: 'ℹ️',
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const loadPlans = async () => {
    try {
      const data = await apiService.getAvailablePlans();
      setPlans(data);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const isDowngrade = (targetPlanId: string): boolean => {
    if (!subscription) return false;
    const currentTier = PLAN_TIERS[subscription.planId] || 0;
    const targetTier = PLAN_TIERS[targetPlanId] || 0;
    return targetTier < currentTier;
  };

  const getButtonConfig = (planId: string) => {
    const isCurrentPlan = subscription?.planId === planId;
    // Subscription status now uses Stripe status values after consolidation
    const isTrialing = subscription?.status === 'trialing';
    const isActive = subscription?.status === 'active';
    const isPastDue = subscription?.status === 'past_due';
    const isCanceled = subscription?.status === 'canceled';

    // If user is trialing this plan, show "Subscribe Now" button to convert trial to paid
    if (isCurrentPlan && isTrialing) {
      return {
        text: 'Subscribe Now',
        enabled: true,
        variant: 'primary',
        badge: 'Currently Trialing',
        badgeColor: 'blue'
      };
    }

    // If user has active subscription for this plan, show "Current Plan"
    if (isCurrentPlan && isActive) {
      return {
        text: 'Current Plan',
        enabled: false,
        variant: 'secondary',
        badge: 'Active',
        badgeColor: 'green'
      };
    }

    // If user is trialing, past due, or canceled and this is a different plan
    if ((isTrialing || isPastDue || isCanceled) && !isCurrentPlan) {
      const isDowngrading = isDowngrade(planId);
      return {
        text: isDowngrading ? `Downgrade to ${plans.find(p => p.id === planId)?.name}` : `Upgrade to ${plans.find(p => p.id === planId)?.name}`,
        enabled: true,
        variant: isDowngrading ? 'warning' : 'primary',
        badge: null,
        badgeColor: null
      };
    }

    // If user has active subscription and this is a different plan
    if (isActive && !isCurrentPlan) {
      const isDowngrading = isDowngrade(planId);
      return {
        text: isDowngrading ? `Downgrade to ${plans.find(p => p.id === planId)?.name}` : `Upgrade to ${plans.find(p => p.id === planId)?.name}`,
        enabled: true,
        variant: isDowngrading ? 'warning' : 'primary',
        badge: null,
        badgeColor: null
      };
    }

    // Default case - no subscription
    return {
      text: planId === 'starter' ? 'Subscribe Now' : 'Start Free Trial',
      enabled: true,
      variant: 'primary',
      badge: null,
      badgeColor: null
    };
  };

  const handlePlanChange = async (plan: SubscriptionPlan) => {
    const isDowngrading = isDowngrade(plan.id);
    setConfirmingPlan({ plan, isDowngrade: isDowngrading });
  };

  const confirmPlanChange = async () => {
    if (!confirmingPlan) return;

    const { plan, isDowngrade } = confirmingPlan;
    setLoading(true);

    try {
      if (isDowngrade) {
        await apiService.downgradePlan(plan.id);
        toast.success(`Successfully downgraded to ${plan.name}!`, {
          duration: 4000,
          icon: '✓',
        });
      } else {
        await apiService.upgradePlan(plan.id);
        toast.success(`Successfully upgraded to ${plan.name}!`, {
          duration: 4000,
          icon: '🎉',
        });
      }
      await refreshSubscription();
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;

      if (status === 403) {
        toast.error('You need administrator privileges to change subscription plans', {
          duration: 6000,
          icon: '🔒',
        });
      } else {
        toast.error(message || `Failed to ${isDowngrade ? 'downgrade' : 'upgrade'}. Please try again.`, {
          duration: 5000,
        });
      }
    } finally {
      setLoading(false);
      setConfirmingPlan(null);
    }
  };

  const handleStartTrial = async () => {
    setLoading(true);
    try {
      await apiService.createTrial();
      await refreshSubscription();
      toast.success('Trial started successfully! Enjoy 14 days of Professional features.', {
        duration: 5000,
        icon: '🎉',
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to start trial', {
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId: string) => {
    try {
      setCheckoutLoading(planId);

      // Use the unified subscription checkout endpoint
      const response = await apiService.createStripeCheckoutSession(
        planId, // Backend will map plan ID to Stripe price ID
        `${window.location.origin}/settings/stripe-billing?success=true&session_id={CHECKOUT_SESSION_ID}`,
        `${window.location.origin}/settings/subscription?canceled=true`
      );

      // Redirect to Stripe checkout
      if (response.success && response.data.url) {
        console.log('Redirecting to Stripe checkout URL:', response.data.url);
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <CreditCard className="h-8 w-8 text-gray-900 dark:text-gray-100" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Subscription & Billing
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage your plan and billing information
            </p>
          </div>
        </div>
      </div>

      {/* Admin Permission Warning */}
      {!isAdmin && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Lock className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                Administrator Access Required
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                You can view subscription details, but only administrators can change subscription plans.
                Please contact your administrator to upgrade or downgrade your subscription.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription */}
      {subscription && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Current Plan</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold text-accent-color">{subscription.planName}</p>
              <p className="text-gray-600 dark:text-gray-400">Status: {subscription.status}</p>
              {subscription.trialEndDate && (
                <p className="text-sm text-gray-500 mt-1">
                  Trial ends: {new Date(subscription.trialEndDate).toLocaleDateString()}
                </p>
              )}
            </div>
            {usageStats && (
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Users: {usageStats.currentUsers}/{usageStats.maxUsers === -1 ? '∞' : usageStats.maxUsers}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Work Orders: Unlimited
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No Subscription - Show Trial Option */}
      {!subscription && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Start Your Free Trial!</h2>
              <p className="text-blue-100">
                Get 14 days of Professional features for free. No credit card required.
              </p>
            </div>
            <button
              onClick={handleStartTrial}
              disabled={loading}
              className="bg-accent-color text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-color-600 transition-colors disabled:opacity-50"
            >
              <Sparkles className="inline h-5 w-5 mr-2" />
              Start Trial
            </button>
          </div>
        </div>
      )}

      {/* Available Plans */}
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 ${
              subscription?.planId === plan.id ? 'ring-2 ring-accent-color' : ''
            }`}
          >
            <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold">${plan.monthlyPrice}</span>
              <span className="text-gray-600 dark:text-gray-400">/month</span>
            </div>

            <ul className="space-y-2 mb-6 text-sm">
              {/* Users */}
              <li className="flex items-start text-gray-700 dark:text-gray-300">
                <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="font-semibold">{plan.maxUsers === -1 ? 'Unlimited users' : `Up to ${plan.maxUsers} users`}</span>
              </li>

              {/* Work Orders */}
              <li className="flex items-start text-gray-700 dark:text-gray-300">
                <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="font-semibold">Unlimited work orders</span>
              </li>

              {/* Core Features (all plans) */}
              <li className="flex items-start text-gray-600 dark:text-gray-400">
                <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Client & vendor management</span>
              </li>
              <li className="flex items-start text-gray-600 dark:text-gray-400">
                <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Work order tracking</span>
              </li>
              <li className="flex items-start text-gray-600 dark:text-gray-400">
                <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>File attachments</span>
              </li>
              <li className="flex items-start text-gray-600 dark:text-gray-400">
                <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Timeline & notes</span>
              </li>

              {/* Starter plan - show what's NOT included */}
              {plan.id === 'starter' && (
                <>
                  <li className="flex items-start text-gray-400 dark:text-gray-500">
                    <X className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Invoicing & billing</span>
                  </li>
                  <li className="flex items-start text-gray-400 dark:text-gray-500">
                    <X className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Reports & analytics</span>
                  </li>
                  <li className="flex items-start text-gray-400 dark:text-gray-500">
                    <X className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>API access</span>
                  </li>
                </>
              )}

              {/* Professional/Enterprise Features */}
              {plan.hasInvoicing && (
                <li className="flex items-start text-gray-700 dark:text-gray-300">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="font-semibold">Invoicing & billing</span>
                </li>
              )}
              {plan.hasReports && (
                <li className="flex items-start text-gray-700 dark:text-gray-300">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="font-semibold">Reports & analytics</span>
                </li>
              )}
              {plan.hasBulkOperations && (
                <li className="flex items-start text-gray-600 dark:text-gray-400">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Bulk operations</span>
                </li>
              )}
              {plan.hasApiAccess && (
                <li className="flex items-start text-gray-700 dark:text-gray-300">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="font-semibold">API access</span>
                </li>
              )}

              {/* Vendor compliance & COI (Enterprise) */}
              {plan.hasVendorCompliance ? (
                <li className="flex items-start text-gray-700 dark:text-gray-300">
                  <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="font-semibold">Vendor compliance & COI tracking</span>
                </li>
              ) : (
                <li className="flex items-start text-gray-400 dark:text-gray-500">
                  <X className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Vendor compliance & COI tracking</span>
                </li>
              )}

              {/* Enterprise-only features */}
              {plan.id === 'enterprise' && (
                <>
                  <li className="flex items-start text-gray-600 dark:text-gray-400">
                    <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Priority support</span>
                  </li>
                  <li className="flex items-start text-gray-600 dark:text-gray-400">
                    <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Advanced security</span>
                  </li>
                </>
              )}
            </ul>

            <div className="space-y-3">
              {/* Badge */}
              {(() => {
                const config = getButtonConfig(plan.id);
                if (config.badge) {
                  const badgeColors = {
                    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                    green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                  };
                  return (
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeColors[config.badgeColor as keyof typeof badgeColors]}`}>
                      {config.badge}
                    </div>
                  );
                }
                return null;
              })()}

              {/* Button */}
              <div className="relative group">
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={checkoutLoading === plan.id || !isAdmin || !getButtonConfig(plan.id).enabled}
                  className={`w-full py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                    getButtonConfig(plan.id).variant === 'primary'
                      ? 'bg-accent-color hover:bg-accent-color-600 text-white disabled:hover:bg-accent-color'
                      : getButtonConfig(plan.id).variant === 'warning'
                      ? 'bg-orange-500 hover:bg-orange-600 text-white disabled:hover:bg-orange-500'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                  title={!isAdmin ? 'Administrator access required' : ''}
                >
                  {checkoutLoading === plan.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : !isAdmin ? (
                    <Lock className="h-4 w-4 mr-1" />
                  ) : null}
                  {checkoutLoading === plan.id ? 'Processing...' : getButtonConfig(plan.id).text}
                </button>
                {!isAdmin && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block">
                    <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                      Administrator access required
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Confirmation Modal */}
      {confirmingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              {confirmingPlan.isDowngrade ? (
                <AlertTriangle className="h-6 w-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
              ) : (
                <Sparkles className="h-6 w-6 text-blue-500 mr-3 flex-shrink-0 mt-1" />
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {confirmingPlan.isDowngrade ? 'Confirm Downgrade' : 'Confirm Upgrade'}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  {confirmingPlan.isDowngrade ? (
                    <>
                      You are about to downgrade to the <strong>{confirmingPlan.plan.name}</strong> plan
                      (${confirmingPlan.plan.monthlyPrice}/month). This will:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Reduce your available features</li>
                        <li>Limit users to {confirmingPlan.plan.maxUsers === -1 ? 'unlimited' : confirmingPlan.plan.maxUsers}</li>
                        {!confirmingPlan.plan.hasInvoicing && <li>Remove invoicing access</li>}
                        {!confirmingPlan.plan.hasReports && <li>Remove reports access</li>}
                      </ul>
                    </>
                  ) : (
                    <>
                      You are about to upgrade to the <strong>{confirmingPlan.plan.name}</strong> plan
                      (${confirmingPlan.plan.monthlyPrice}/month). You'll get:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>{confirmingPlan.plan.maxUsers === -1 ? 'Unlimited' : confirmingPlan.plan.maxUsers} users</li>
                        {confirmingPlan.plan.hasInvoicing && <li>Full invoicing features</li>}
                        {confirmingPlan.plan.hasReports && <li>Reports & analytics</li>}
                        {confirmingPlan.plan.hasApiAccess && <li>API access</li>}
                      </ul>
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setConfirmingPlan(null)}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmPlanChange}
                disabled={loading}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                  confirmingPlan.isDowngrade
                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                    : 'bg-accent-color hover:bg-accent-color-600 text-white'
                }`}
              >
                {loading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;


